import * as React from "react";
import { useLocalStorage } from "react-use";
import { compareAsc, parseISO } from "date-fns";
import type { Item } from "@/types/fse";
import type { SortOption } from "@/components/sortDropdown";
import { DEFAULT_DRIVE_SORT } from "@/views/drive/workspace/constants";

const defaultSortItems = (items: Item[], sortConfig: SortOption): Item[] =>
  [...items].sort((a, b) => {
    if (sortConfig.field === "name") {
      const aValue = a.name.toLowerCase();
      const bValue = b.name.toLowerCase();
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    }
    const comparison = compareAsc(parseISO(a.created_at), parseISO(b.created_at));
    return sortConfig.direction === "asc" ? comparison : -comparison;
  });

export const useSortedItems = (
  items: Item[],
  storageKey = "sorted-items-config",
  sortItems: (items: Item[], sort: SortOption) => Item[] = defaultSortItems,
) => {
  const [currentSort, setCurrentSort] = useLocalStorage<SortOption>(storageKey, DEFAULT_DRIVE_SORT);
  const sortConfig = currentSort || DEFAULT_DRIVE_SORT;

  const sortedItems = React.useMemo(() => sortItems(items, sortConfig), [items, sortConfig, sortItems]);

  return {
    sortedItems,
    currentSort: sortConfig,
    setCurrentSort,
  };
};
